import { BehaviorSubject } from 'rxjs';
import { AxiosProgressEvent } from 'axios';
import { addError, clearError } from './Error';
import {
  getAttachmentConfig,
  getAttachments,
  uploadAttachmentData,
  archiveAttachmentData,
  downloadAttachmentData
} from '../Api/Attachment/attachmentApi';
import { ALLOWED_EXTENSIONS, MAX_FILE_SIZE_BYTES } from './attachment.constants';
import { User } from './User';
import { downloadBlob, formatFileSize } from '../utils/helpers';

export type Attachment = {
  id: string;
  entityId: string;
  entityType: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdBy?: User;
  createdAt?: string;
};

export type ProgressHandler = (progressEvent: AxiosProgressEvent) => void;

export type AttachmentUploadConfig = {
  maxSizeBytes: number;
  allowedExtensions: string[];
};

export const FALLBACK_UPLOAD_CONFIG: AttachmentUploadConfig = {
  maxSizeBytes: MAX_FILE_SIZE_BYTES,
  allowedExtensions: ALLOWED_EXTENSIONS
};

export const validateAttachmentFile = (file: File, config: AttachmentUploadConfig): string | null => {
  if (file.size > config.maxSizeBytes) return `exceeds the ${formatFileSize(config.maxSizeBytes)} size limit`;
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!extension || !config.allowedExtensions.includes(extension))
    return `file type not allowed (.${extension ?? 'unknown'})`;
  return null;
};

export const partitionAttachmentFiles = (
  files: File[],
  config: AttachmentUploadConfig
): { validFiles: File[]; rejections: string[] } => {
  const validFiles: File[] = [];
  const rejections: string[] = [];
  files.forEach((file) => {
    const validationError = validateAttachmentFile(file, config);
    if (validationError) {
      rejections.push(`${file.name}: ${validationError}`);
    } else {
      validFiles.push(file);
    }
  });
  return { validFiles, rejections };
};

let uploadConfigPromise: Promise<AttachmentUploadConfig> | null = null;

export const fetchAttachmentUploadConfig = (): Promise<AttachmentUploadConfig> => {
  if (!uploadConfigPromise) {
    uploadConfigPromise = getAttachmentConfig().then(({ data, error }) => {
      if (error || !data) {
        uploadConfigPromise = null;
        return FALLBACK_UPLOAD_CONFIG;
      }
      return data;
    });
  }
  return uploadConfigPromise;
};

const attachmentsMap = new Map<string, BehaviorSubject<Attachment[]>>();
const attachmentsLoadingMap = new Map<string, BehaviorSubject<boolean>>();

export const getAttachmentSubject = (entityId: string, entityType: string): BehaviorSubject<Attachment[]> => {
  const key = `${entityId}-${entityType}`;
  let subject = attachmentsMap.get(key);
  if (!subject) {
    subject = new BehaviorSubject<Attachment[]>([]);
    attachmentsMap.set(key, subject);
  }
  return subject;
};

export const getAttachmentLoadingSubject = (entityId: string, entityType: string): BehaviorSubject<boolean> => {
  const key = `${entityId}-${entityType}`;
  let subject = attachmentsLoadingMap.get(key);
  if (!subject) {
    subject = new BehaviorSubject<boolean>(false);
    attachmentsLoadingMap.set(key, subject);
  }
  return subject;
};

export const fetchAttachments = async (entityId: string, entityType: string) => {
  const loadingSubject = getAttachmentLoadingSubject(entityId, entityType);
  const attachmentsSubject = getAttachmentSubject(entityId, entityType);

  if (loadingSubject.getValue()) return;
  loadingSubject.next(true);

  const valueBeforeFetch = attachmentsSubject.getValue();
  const { data, error } = await getAttachments(entityId, entityType);

  if (data) {
    clearError('attachment');
    const currentValue = attachmentsSubject.getValue();
    if (currentValue === valueBeforeFetch) {
      attachmentsSubject.next(data);
    } else {
      const addedSinceFetch = currentValue.filter((item) => !data.some((fetched) => fetched.id === item.id));
      attachmentsSubject.next([...addedSinceFetch, ...data]);
    }
  }
  if (error) addError('attachment', error.message);

  loadingSubject.next(false);
};

export const uploadAttachment = async (
  file: File,
  entityId: string,
  entityType: string,
  progressHandler: ProgressHandler,
  signal?: AbortSignal
): Promise<Attachment | null> => {
  if (signal?.aborted) return null;

  const { data, error } = await uploadAttachmentData(file, entityId, entityType, progressHandler, signal);

  if (data) {
    clearError('attachment');
    const attachmentsSubject = getAttachmentSubject(entityId, entityType);
    attachmentsSubject.next([data, ...attachmentsSubject.getValue()]);
  }
  if (error && !signal?.aborted) addError('attachment', error.message);

  return data;
};

export const archiveAttachment = async (attachment: Attachment) => {
  const { error } = await archiveAttachmentData(attachment.id);
  const attachmentsSubject = getAttachmentSubject(attachment.entityId, attachment.entityType);

  if (!error) {
    clearError('attachment');
    attachmentsSubject.next(attachmentsSubject.getValue().filter((a) => a.id !== attachment.id));
  } else {
    addError('attachment', error.message);
  }
};

export const downloadAttachment = async (attachment: Attachment) => {
  const { data, error } = await downloadAttachmentData(attachment.id);

  if (data) {
    clearError('attachment');
    downloadBlob(data, attachment.filename);
  }
  if (error) addError('attachment', error.message);
};
